'use client';

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';

interface StreamingMessageProps {
  content: string;
  isComplete?: boolean;
}

// Define components outside the component to prevent recreation
const MarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="whitespace-pre-wrap mb-4 last:mb-0">{children}</p>
  ),
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold mb-4">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-bold mb-3">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-bold mb-2">{children}</h3>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-6 mb-4" role="list">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 mb-4" role="list">{children}</ol>
  ),
  li: ({ children }) => <li className="mb-2">{children}</li>,
  code: ({ children }) => (
    <code className="bg-gray-700 px-1 rounded">{children}</code>
  ),
  hr: () => <hr className="my-6 border-gray-700" aria-hidden="true" />,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-gray-500 pl-4 italic my-4">
      {children}
    </blockquote>
  ),
  // Security: sanitize links to prevent XSS
  a: ({ href, children }) => {
    // Only allow http, https, mailto, and tel protocols
    const safeHref = href && /^(https?:|mailto:|tel:)/.test(href) ? href : '#';
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent-600 underline hover:text-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2"
      >
        {children}
      </a>
    );
  },
  // Security: sanitize images to prevent XSS via onerror
  img: ({ src, alt }) => {
    // Only allow http, https protocols for images
    const safeSrc = typeof src === 'string' && /^(https?:)/.test(src) ? src : '';
    if (!safeSrc) return null;
    return (
      <img
        src={safeSrc}
        alt={alt || ''}
        className="max-w-full h-auto rounded my-2"
        loading="lazy"
      />
    );
  },
};

const StreamingMessage: React.FC<StreamingMessageProps> = ({
  content,
  isComplete,
}) => {
  // Memoize the content with cursor to prevent unnecessary rerenders
  const displayContent = useMemo(
    () => (content || '') + (!isComplete ? '\u258C' : ''),
    [content, isComplete]
  );

  return (
    <div aria-live={isComplete ? 'off' : 'polite'} aria-atomic="false">
      <ReactMarkdown components={MarkdownComponents}>
        {displayContent}
      </ReactMarkdown>
    </div>
  );
};

export default React.memo(StreamingMessage);
